/**
 * Impersonation handoff (SuperUser only).
 *
 * A SuperUser can open the customer PORTAL or the CONTACT CENTER as one of an
 * account's SSO users. The gateway already supports it via the
 * `X-Impersonate-User: <KeycloakId>` header (honoured only when the bearer
 * token carries the SuperUser role); the portal / contact center read a small
 * handoff blob from `localStorage` (same origin, notify.roommatik.com) and
 * re-issue every request with that header on the SuperUser's own backoffice
 * token.
 *
 * The handoff carries the *current* backoffice token (the same `Authorization`
 * httpClient uses) plus the target user's Keycloak id — which is what the
 * gateway matches against `Users.KeycloakId`.
 */

import { resolveAuthToken } from '@/utils/httpClient';
import sso from '@/auth/sso';

/** localStorage key the portal / contact center read on boot. */
export const IMPERSONATION_STORAGE_KEY = 'notify_impersonation';

export type ImpersonationTarget = 'portal' | 'agent';

export interface ImpersonationHandoff {
  /** Fresh backoffice bearer token of the acting SuperUser. */
  token: string;
  /** Keycloak id of the impersonated user (gateway: `Users.KeycloakId`). */
  userSub: string;
  /** Display name / email of the impersonated user (for the banner). */
  userName: string;
  /** Name of the account the impersonated user belongs to. */
  accountName: string;
}

/** Same-origin path of each destination app. */
const DESTINATION_PATH: Record<ImpersonationTarget, string> = {
  portal: '/portal/',
  agent: '/agent/',
};

/**
 * Persist the handoff and open the chosen destination in a new tab.
 *
 * Reads a *fresh* token via `resolveAuthToken()` (preflights the 30 s SSO
 * refresh) and falls back to the persisted token for the brief window before
 * the interceptor is enabled.
 */
export const openImpersonation = async (
  target: ImpersonationTarget,
  user: { userSub: string; userName: string; accountName: string },
): Promise<void> => {
  const token =
    (await resolveAuthToken()) ??
    localStorage.getItem('authToken') ??
    sso.token ??
    null;

  if (!token) {
    throw new Error('No backoffice token available for impersonation.');
  }

  const handoff: ImpersonationHandoff = {
    token,
    userSub: user.userSub,
    userName: user.userName,
    accountName: user.accountName,
  };

  localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(handoff));
  window.open(DESTINATION_PATH[target], '_blank', 'noopener,noreferrer');
};
